import { useState, useEffect } from "react";
import { suppliersAPI, usersAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Building2, User, Search, Phone, Mail, CreditCard, Link } from "lucide-react";

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  
  const [formData, setFormData] = useState({
    supplier_type: "vertriebler",
    salutation: "Herr",
    first_name: "",
    last_name: "",
    company_name: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    email: "",
    tax_number: "",
    vat_id: "",
    bank_name: "",
    bank_iban: "",
    bank_bic: "",
    linked_user_id: "",
    notes: ""
  });

  useEffect(() => {
    loadSuppliers();
    loadUsers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await suppliersAPI.list();
      setSuppliers(response.data);
    } catch (error) {
      console.error("Failed to load suppliers:", error);
      toast.error("Fehler beim Laden der Lieferanten");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.list();
      // Filter only vertriebler
      const vertriebler = response.data.filter(u => u.role === "vertriebler");
      setUsers(vertriebler);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_type: "vertriebler",
      salutation: "Herr",
      first_name: "",
      last_name: "",
      company_name: "",
      address: "",
      postal_code: "",
      city: "",
      phone: "",
      email: "",
      tax_number: "",
      vat_id: "",
      bank_name: "",
      bank_iban: "",
      bank_bic: "",
      linked_user_id: "",
      notes: ""
    });
    setEditingSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.linked_user_id) delete data.linked_user_id;
      
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, data);
        toast.success("Lieferant aktualisiert");
      } else {
        await suppliersAPI.create(data);
        toast.success("Lieferant erstellt");
      }
      setShowDialog(false);
      resetForm();
      loadSuppliers();
    } catch (error) {
      console.error("Failed to save supplier:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_type: supplier.supplier_type || "vertriebler",
      salutation: supplier.salutation || "Herr",
      first_name: supplier.first_name || "",
      last_name: supplier.last_name || "",
      company_name: supplier.company_name || "",
      address: supplier.address || "",
      postal_code: supplier.postal_code || "",
      city: supplier.city || "",
      phone: supplier.phone || "",
      email: supplier.email || "",
      tax_number: supplier.tax_number || "",
      vat_id: supplier.vat_id || "",
      bank_name: supplier.bank_name || "",
      bank_iban: supplier.bank_iban || "",
      bank_bic: supplier.bank_bic || "",
      linked_user_id: supplier.linked_user_id || "",
      notes: supplier.notes || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`Möchten Sie den Lieferanten "${supplier.first_name} ${supplier.last_name}" wirklich löschen?`)) return;
    
    try {
      await suppliersAPI.delete(supplier.id);
      toast.success("Lieferant gelöscht");
      loadSuppliers();
    } catch (error) {
      console.error("Failed to delete supplier:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = 
      supplier.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.supplier_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === "all" || supplier.supplier_type === filterType;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Lieferanten</h1>
          <p className="text-muted-foreground">Vertriebler und Tippgeber verwalten</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" />
          Neuer Lieferant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="supplier-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="supplier-type-filter">
            <SelectValue placeholder="Alle Typen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            <SelectItem value="vertriebler">Vertriebler</SelectItem>
            <SelectItem value="tippgeber">Tippgeber</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Suppliers List */}
      <div className="grid gap-4">
        {filteredSuppliers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Lieferanten gefunden</p>
            </CardContent>
          </Card>
        ) : (
          filteredSuppliers.map((supplier) => (
            <Card key={supplier.id} className="hover:shadow-md transition-shadow" data-testid={`supplier-card-${supplier.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">
                        {supplier.company_name || `${supplier.first_name} ${supplier.last_name}`}
                      </CardTitle>
                      <Badge variant={supplier.supplier_type === "vertriebler" ? "default" : "secondary"}>
                        {supplier.supplier_type === "vertriebler" ? "Vertriebler" : "Tippgeber"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{supplier.supplier_number}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(supplier)} data-testid={`edit-supplier-${supplier.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(supplier)} className="text-destructive" data-testid={`delete-supplier-${supplier.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.salutation} {supplier.first_name} {supplier.last_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.postal_code} {supplier.city}</span>
                  </div>
                  {supplier.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{supplier.email}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                  {supplier.bank_iban && (
                    <div className="flex items-center gap-2 col-span-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span>{supplier.bank_name}: {supplier.bank_iban}</span>
                    </div>
                  )}
                  {supplier.linked_user_name && (
                    <div className="flex items-center gap-2">
                      <Link className="h-4 w-4 text-muted-foreground" />
                      <span>Verknüpft: {supplier.linked_user_name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? "Lieferant bearbeiten" : "Neuer Lieferant"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={formData.supplier_type} onValueChange={(v) => setFormData({ ...formData, supplier_type: v })}>
                  <SelectTrigger data-testid="supplier-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vertriebler">Vertriebler</SelectItem>
                    <SelectItem value="tippgeber">Tippgeber</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anrede</Label>
                <Select value={formData.salutation} onValueChange={(v) => setFormData({ ...formData, salutation: v })}>
                  <SelectTrigger data-testid="supplier-salutation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Divers">Divers</SelectItem>
                    <SelectItem value="Firma">Firma</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.salutation === "Firma" && (
              <div className="space-y-2">
                <Label>Firmenname</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  data-testid="supplier-company-name"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname *</Label>
                <Input
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="supplier-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname *</Label>
                <Input
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="supplier-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Straße und Hausnummer *</Label>
              <Input
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="supplier-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PLZ *</Label>
                <Input
                  required
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  data-testid="supplier-postal-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Stadt *</Label>
                <Input
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="supplier-city"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="supplier-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="supplier-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Steuer-Nr.</Label>
                <Input
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  data-testid="supplier-tax-number"
                />
              </div>
              <div className="space-y-2">
                <Label>USt-ID</Label>
                <Input
                  value={formData.vat_id}
                  onChange={(e) => setFormData({ ...formData, vat_id: e.target.value })}
                  data-testid="supplier-vat-id"
                />
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Bankverbindung</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    data-testid="supplier-bank-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input
                      value={formData.bank_iban}
                      onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                      data-testid="supplier-bank-iban"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>BIC</Label>
                    <Input
                      value={formData.bank_bic}
                      onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value })}
                      data-testid="supplier-bank-bic"
                    />
                  </div>
                </div>
              </div>
            </div>

            {formData.supplier_type === "vertriebler" && users.length > 0 && (
              <div className="space-y-2">
                <Label>Mit Vertriebler-Benutzer verknüpfen</Label>
                <Select value={formData.linked_user_id || "none"} onValueChange={(v) => setFormData({ ...formData, linked_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="supplier-linked-user">
                    <SelectValue placeholder="Keine Verknüpfung" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Verknüpfung</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                data-testid="supplier-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="save-supplier-btn">
                {editingSupplier ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
